/**
 * Response styles — lightweight presets that shape tone and thoroughness.
 * Not separate agents; just instruction overlays on the composer.
 */
import { Scale, Telescope, ClipboardCheck, Zap } from 'lucide-react';

export type PersonaId = 'default' | 'researcher' | 'reviewer' | 'quick';

/** Icon per style — shared by the composer picker and Settings. */
export const PERSONA_ICONS: Record<PersonaId, typeof Scale> = {
  default: Scale,
  researcher: Telescope,
  reviewer: ClipboardCheck,
  quick: Zap,
};

export interface PersonaDef {
  id: PersonaId;
  label: string;
  /** Short chip label */
  short: string;
  description: string;
  /**
   * Extra system block injected after global instructions.
   * Empty for default (no extra layer).
   */
  systemBlock: string;
}

export const PERSONAS: PersonaDef[] = [
  {
    id: 'default',
    label: 'Default',
    short: 'Default',
    description: 'Balanced — your global and project instructions only.',
    systemBlock: '',
  },
  {
    id: 'researcher',
    label: 'Research',
    short: 'Research',
    description: 'Thorough; cites project context; flags uncertainty.',
    systemBlock: [
      '# Style: Research',
      'Prioritize accuracy over speed. When project memory or context is present, cite it explicitly (e.g. “From project memory…” / “From context: …”).',
      'If evidence is thin or conflicting, say so. Prefer structured answers with sources over confident guesses.',
      'Ask a clarifying question only when it would materially change the answer.',
    ].join('\n'),
  },
  {
    id: 'reviewer',
    label: 'Review',
    short: 'Review',
    description: 'Critical, concise, checklist-style feedback.',
    systemBlock: [
      '# Style: Review',
      'Act as a careful reviewer. Be direct and concise. Lead with findings, then severity, then suggested fixes.',
      'Prefer bullet lists and checklists. Challenge weak assumptions. Do not pad with praise.',
      'When project instructions define standards, enforce them.',
    ].join('\n'),
  },
  {
    id: 'quick',
    label: 'Quick',
    short: 'Quick',
    description: 'Short answers; minimal preamble.',
    systemBlock: [
      '# Style: Quick',
      'Answer in as few words as practical. Skip preamble and recap unless asked.',
      'Use bullets when listing. Default to one short paragraph or a tight list.',
    ].join('\n'),
  },
];

export function getPersona(id: PersonaId | string | undefined | null): PersonaDef {
  const found = PERSONAS.find((p) => p.id === id);
  return found ?? PERSONAS[0]!;
}

export function isPersonaId(v: unknown): v is PersonaId {
  return typeof v === 'string' && PERSONAS.some((p) => p.id === v);
}
